import mapboxgl, { LngLatBoundsLike, Map } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_PATTERN_DD_MMM_YYYY_NODASH, convertToDateJakartaTime, toIndoFormat } from '../../helper/helper';
import { FruitTraceability } from '../../models/traceability/FruitTraceability';
import './PurchaseMap.scss';
import {
    averageDistanceKm,
    buildAllSources,
    FLOW_COLORS,
    flowLines,
    formatCoordinates,
    MapSource,
    millCoordinates,
    sourcePlots,
    sourcePoints,
    supplyZone,
    supplyZoneRadiusKm,
} from './contractFlow';
import { ExpandIcon } from './PurchaseReportIcons';
import { TRACEABILITY_TYPES, deforestationPillClass, getTraceabilityData } from './traceabilityTypes';

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default; /* eslint import/no-webpack-loader-syntax: off */

type Props = {
    contracts: FruitTraceability[]
    /** The mill every flow line converges on. */
    mill: any
    /** null shows the whole period; an id focuses that contract. */
    selectedContractId: number | null
    onSelectContract: (contractId: number) => void
    onClearSelection: () => void
    expanded: boolean
    onToggleExpanded: () => void
    /** Changes whenever the surrounding tab becomes visible, so the canvas can re-measure. */
    resize?: any
    /** Filled with a function returning the rendered map as PNG, for the monthly report. */
    captureRef?: React.MutableRefObject<(() => Promise<Blob | null>) | null>
}

const SRC_FLOWS = 'purchase-flows';
const SRC_POINTS = 'purchase-points';
const SRC_PLOTS = 'purchase-plots';
const SRC_ZONE = 'purchase-zone';

const LYR_ZONE_FILL = 'purchase-zone-fill';
const LYR_ZONE_LINE = 'purchase-zone-line';
const LYR_FLOW = 'purchase-flow';
const LYR_FLOW_DASH = 'purchase-flow-dash';
const LYR_PLOT = 'purchase-plot';
const LYR_POINT = 'purchase-point';

/**
 * Plain OpenStreetMap raster tiles — no Mapbox-hosted style, so the map needs no
 * access token and carries the OSM attribution and nothing else. Note there is
 * no `glyphs` entry: symbol layers would need one, so every label on this map is
 * a DOM marker instead.
 */
const OSM_STYLE: any = {
    version: 8,
    sources: {
        osm: {
            type: 'raster',
            tiles: [
                'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
        },
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

/**
 * Dash patterns that, stepped through in order, walk the gaps along the line.
 * Only the focused contract's flows are dashed, so the animation never competes
 * with the overview.
 */
const DASH_SEQUENCE: number[][] = [
    [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5], [2, 4, 1], [2.5, 4, 0.5],
    [3, 4, 0], [0, 0.5, 3, 3.5], [0, 1, 3, 3], [0, 1.5, 3, 2.5], [0, 2, 3, 2],
    [0, 2.5, 3, 1.5], [0, 3, 3, 1], [0, 3.5, 3, 0.5],
];

const DASH_STEP_MS = 55;

/** The resting dash pattern, carried by every flow line whether focused or not. */
const FLOW_DASH: [number, number] = [2, 2];

/** Indonesia, so an empty map does not open on null island. */
const FALLBACK_CENTER: [number, number] = [101.378757, 0.41466];

const emptyCollection = (): any => ({ type: 'FeatureCollection', features: [] });

const setData = (map: Map, id: string, data: any) =>
    (map.getSource(id) as mapboxgl.GeoJSONSource)?.setData(data);

/**
 * A pin is a bubble plus a pointer, both inside one box whose bottom edge is the
 * pointer's tip — so `anchor: 'bottom'` lands the tip exactly on the coordinate.
 * The pointer must not be a margin: mapbox's marker wrapper collapses it away.
 */
const pinElement = (modifier: string): { pin: HTMLElement, bubble: HTMLElement } => {
    const pin = document.createElement('div');
    pin.className = `flow-pin flow-pin--${modifier}`;

    const bubble = document.createElement('div');
    bubble.className = 'flow-pin__bubble';
    pin.appendChild(bubble);

    return { pin, bubble };
};

const letterPin = (source: MapSource): HTMLElement => {
    const { pin, bubble } = pinElement('source');
    bubble.innerText = source.letter;
    return pin;
};

const millPin = (title: string): HTMLElement => {
    const { pin, bubble } = pinElement('mill');
    pin.title = title;

    const icon = document.createElement('img');
    icon.src = '/ic_mill.svg';
    icon.alt = '';
    bubble.appendChild(icon);

    return pin;
};

const distanceLabel = (km: number): HTMLElement => {
    const label = document.createElement('div');
    label.className = 'flow-distance';
    label.innerText = `${km.toFixed(1)} km`;
    return label;
};

const midpoint = (a: [number, number], b: [number, number]): [number, number] =>
    [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

/** Above this, per-line distance labels turn into confetti; the legend carries them instead. */
const MAX_DISTANCE_LABELS = 6;

export function PurchaseMap({
    contracts,
    mill,
    selectedContractId,
    onSelectContract,
    onClearSelection,
    expanded,
    onToggleExpanded,
    resize,
    captureRef,
}: Props) {
    const { t } = useTranslation();

    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<Map>(null);
    const markers = useRef<mapboxgl.Marker[]>([]);
    const animation = useRef<number>(null);
    // Sources can only be added once the style has loaded, so the data effects wait on this.
    const [mapReady, setMapReady] = useState(false);
    // Layer click handlers are bound once on load, so they reach the latest callback via a ref.
    const selectRef = useRef(onSelectContract);
    selectRef.current = onSelectContract;

    const millCoords = useMemo(() => millCoordinates(mill), [mill]);
    const allSources = useMemo(() => buildAllSources(contracts, millCoords), [contracts, millCoords]);

    const selectedContract = useMemo(
        () => contracts.find(c => c.contract_id === selectedContractId),
        [contracts, selectedContractId]
    );

    const focusedSources = useMemo(
        () => (selectedContract ? allSources.filter(s => s.contractId === selectedContract.contract_id) : []),
        [allSources, selectedContract]
    );

    /** What the legend and the title bar describe: the focused contract, or the whole period. */
    const scopeSources = selectedContract ? focusedSources : allSources;
    const avgKm = useMemo(() => averageDistanceKm(scopeSources), [scopeSources]);
    const zoneKm = useMemo(() => supplyZoneRadiusKm(allSources), [allSources]);

    // Initialise once; the style is static, only the data below changes.
    useEffect(() => {
        if (map.current || !mapContainer.current) return;

        try {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: OSM_STYLE,
                center: millCoords ?? FALLBACK_CENTER,
                zoom: 9,
                attributionControl: false,
            });
            map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
            map.current.on('load', () => {
                addLayers(map.current);
                bindSourceClicks(map.current);
                startDashAnimation();
                setMapReady(true);
            });
        } catch (e) {
            // WebGL unavailable — the legend beside the map still carries every coordinate.
        }

        return () => {
            if (animation.current !== null) cancelAnimationFrame(animation.current);
            markers.current.forEach(m => m.remove());
            markers.current = [];
            map.current?.remove();
            map.current = null;
            setMapReady(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The WebGL buffer is cleared after each frame, so read it inside a render
    // callback rather than paying for preserveDrawingBuffer. DOM markers
    // (mill pin, source letters, distance labels) are not part of the canvas.
    useEffect(() => {
        if (!captureRef) return;
        captureRef.current = () => new Promise(resolve => {
            const instance = map.current;
            if (!instance || !mapReady) return resolve(null);
            instance.once('render', () => instance.getCanvas().toBlob(resolve, 'image/png'));
            instance.triggerRepaint();
        });
        return () => { captureRef.current = null; };
    }, [captureRef, mapReady]);

    const addLayers = (instance: Map) => {
        [SRC_ZONE, SRC_FLOWS, SRC_PLOTS, SRC_POINTS].forEach(id =>
            instance.addSource(id, { type: 'geojson', data: emptyCollection() }));

        instance.addLayer({
            id: LYR_ZONE_FILL,
            type: 'fill',
            source: SRC_ZONE,
            paint: { 'fill-color': FLOW_COLORS.zone, 'fill-opacity': 0.04 },
        });
        instance.addLayer({
            id: LYR_ZONE_LINE,
            type: 'line',
            source: SRC_ZONE,
            paint: { 'line-color': FLOW_COLORS.zone, 'line-width': 1.5, 'line-dasharray': [3, 3], 'line-opacity': 0.55 },
        });

        // Every contract's flows, dimmed to context weight once one is focused.
        // Butt caps, because round ones stretch each dash back into a solid line.
        instance.addLayer({
            id: LYR_FLOW,
            type: 'line',
            source: SRC_FLOWS,
            layout: { 'line-cap': 'butt' },
            paint: {
                'line-color': FLOW_COLORS.flow,
                'line-width': 1.2,
                'line-opacity': 0.3,
                'line-dasharray': FLOW_DASH,
            },
        });
        // Only the focused contract's flows animate; the filter is set with the data.
        instance.addLayer({
            id: LYR_FLOW_DASH,
            type: 'line',
            source: SRC_FLOWS,
            filter: ['==', ['get', 'contractId'], -1],
            layout: { 'line-cap': 'butt' },
            paint: {
                'line-color': FLOW_COLORS.flow,
                'line-width': 2.5,
                'line-dasharray': DASH_SEQUENCE[0] as any,
            },
        });

        instance.addLayer({
            id: LYR_PLOT,
            type: 'fill',
            source: SRC_PLOTS,
            paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.6 },
        });
        instance.addLayer({
            id: LYR_POINT,
            type: 'circle',
            source: SRC_POINTS,
            paint: {
                'circle-color': ['get', 'color'],
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1.5,
                'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 3, 10, 5, 14, 8],
            },
        });
    };

    const bindSourceClicks = (instance: Map) => {
        [LYR_POINT, LYR_PLOT].forEach(layer => {
            instance.on('click', layer, e => {
                const contractId = e.features?.[0]?.properties?.contractId;
                if (contractId !== undefined) selectRef.current(Number(contractId));
            });
            instance.on('mouseenter', layer, () => { instance.getCanvas().style.cursor = 'pointer'; });
            instance.on('mouseleave', layer, () => { instance.getCanvas().style.cursor = ''; });
        });
    };

    /** Walks DASH_SEQUENCE on a fixed cadence so the dashes crawl towards the mill. */
    const startDashAnimation = () => {
        let step = 0;
        let lastFrame = 0;

        const frame = (timestamp: number) => {
            if (timestamp - lastFrame > DASH_STEP_MS) {
                lastFrame = timestamp;
                step = (step + 1) % DASH_SEQUENCE.length;
                if (map.current?.getLayer(LYR_FLOW_DASH)) {
                    map.current.setPaintProperty(LYR_FLOW_DASH, 'line-dasharray', DASH_SEQUENCE[step]);
                }
            }
            animation.current = requestAnimationFrame(frame);
        };

        animation.current = requestAnimationFrame(frame);
    };

    // Geometry for the whole period — independent of which contract is focused.
    useEffect(() => {
        const instance = map.current;
        if (!instance || !mapReady) return;

        setData(instance, SRC_FLOWS, flowLines(allSources, millCoords));
        setData(instance, SRC_POINTS, sourcePoints(allSources));
        setData(instance, SRC_PLOTS, sourcePlots(allSources));
        setData(instance, SRC_ZONE, supplyZone(allSources, millCoords));
    }, [mapReady, allSources, millCoords]);

    // Focus: emphasise one contract without hiding the rest.
    useEffect(() => {
        const instance = map.current;
        if (!instance || !mapReady) return;

        const focusedId = selectedContract?.contract_id ?? -1;
        const isFocused = ['==', ['get', 'contractId'], focusedId];

        instance.setFilter(LYR_FLOW_DASH, isFocused as any);
        instance.setPaintProperty(LYR_FLOW, 'line-opacity',
            selectedContract ? (['case', isFocused, 0.8, 0.1] as any) : 0.3);
        // The focused sources are drawn as lettered pins instead, so drop their dots.
        instance.setFilter(LYR_POINT,
            selectedContract ? (['!=', ['get', 'contractId'], focusedId] as any) : null);
        instance.setPaintProperty(LYR_POINT, 'circle-opacity', selectedContract ? 0.3 : 1);
        instance.setPaintProperty(LYR_POINT, 'circle-stroke-opacity', selectedContract ? 0.3 : 1);
        // The supply zone describes the period, so it only clutters a focused view.
        const zoneVisibility = selectedContract ? 'none' : 'visible';
        instance.setLayoutProperty(LYR_ZONE_FILL, 'visibility', zoneVisibility);
        instance.setLayoutProperty(LYR_ZONE_LINE, 'visibility', zoneVisibility);
    }, [mapReady, selectedContract]);

    // Markers: mill always, lettered pins and per-line distances only when focused.
    useEffect(() => {
        const instance = map.current;
        if (!instance || !mapReady) return;

        markers.current.forEach(m => m.remove());
        markers.current = [];

        if (millCoords) {
            markers.current.push(
                new mapboxgl.Marker({ element: millPin(t('peta.pin.mill')), anchor: 'bottom' })
                    .setLngLat(millCoords)
                    .addTo(instance)
            );
        }

        focusedSources.forEach(source => {
            markers.current.push(
                new mapboxgl.Marker({ element: letterPin(source), anchor: 'bottom' })
                    .setLngLat(source.coordinates)
                    .addTo(instance)
            );

            if (millCoords && source.distanceKm !== undefined && focusedSources.length <= MAX_DISTANCE_LABELS) {
                markers.current.push(
                    new mapboxgl.Marker({ element: distanceLabel(source.distanceKm) })
                        .setLngLat(midpoint(source.coordinates, millCoords))
                        .addTo(instance)
                );
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapReady, focusedSources, millCoords]);

    // Frame whatever is in scope.
    useEffect(() => {
        const instance = map.current;
        if (!instance || !mapReady) return;

        const points = [...scopeSources.map(s => s.coordinates), ...(millCoords ? [millCoords] : [])];
        if (points.length === 1) {
            instance.easeTo({ center: points[0], zoom: 13, duration: 600 });
        } else if (points.length > 1) {
            const bounds = points.reduce(
                (acc, point) => acc.extend(point),
                new mapboxgl.LngLatBounds(points[0], points[0])
            );
            instance.fitBounds(bounds as LngLatBoundsLike, { padding: 56, maxZoom: 14, duration: 600 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapReady, selectedContract, allSources, millCoords]);

    useEffect(() => {
        if (!map.current) return;
        const timer = setTimeout(() => map.current?.resize(), 180);
        return () => clearTimeout(timer);
    }, [resize, expanded]);

    const title = selectedContract
        ? t('peta.map_title', { id: selectedContract.contract_id, n: focusedSources.length })
        : t('peta.map_title_all', { c: contracts.length, n: allSources.length });

    return (
        <section className="purchase-map">
            <header className="purchase-map__bar">
                <span className="purchase-map__title">{title}</span>

                <div className="purchase-map__actions">
                    {selectedContract && (
                        <button type="button" className="purchase-map__chip" onClick={onClearSelection}>
                            ← {t('peta.all_contracts')}
                        </button>
                    )}
                    <button
                        type="button"
                        className="purchase-map__icon-btn"
                        aria-pressed={expanded}
                        title={expanded ? t('peta.collapse_map') : t('peta.expand_map')}
                        onClick={onToggleExpanded}
                    >
                        <ExpandIcon expanded={expanded} />
                    </button>
                </div>
            </header>

            {selectedContract && (
                <div className="purchase-map__detail">
                    <span className="purchase-map__fact">
                        <em>{t('delivery_date')}</em>
                        {convertToDateJakartaTime(selectedContract.market_day, DATE_PATTERN_DD_MMM_YYYY_NODASH)}
                    </span>
                    <span className="purchase-map__fact">
                        <em>{t('traceable_to')}</em>
                        <span className={`ds-pill ds-pill--trace ${getTraceabilityData(selectedContract.traceabilityLabel()).pillClass}`}>
                            <span className="ds-pill__dot" />
                            {getTraceabilityData(selectedContract.traceabilityLabel()).label}
                        </span>
                    </span>
                    <span className="purchase-map__fact">
                        <em>{t('deforestation')}</em>
                        <span className={`ds-pill ds-pill--risk ${deforestationPillClass(selectedContract.deforestationRawLabel())}`}>
                            {t(selectedContract.deforestationLabel())}
                        </span>
                    </span>
                    <span className="purchase-map__fact">
                        <em>{t('quantity')}</em>
                        {toIndoFormat(selectedContract.quantity)}
                    </span>
                    <span className="purchase-map__fact">
                        <em>{t('price')}</em>
                        {toIndoFormat(selectedContract.price)}
                    </span>
                </div>
            )}

            <div className="purchase-map__canvas" ref={mapContainer} />

            <div className="purchase-map__attribution">
                <span>{t('peta.meta')}</span>
                <span>{t('peta.attribution')}</span>
            </div>

            <div className="purchase-map__legend">
                {selectedContract ? (
                    <ul className="purchase-map__keys">
                        {focusedSources.map(source => (
                            <li key={`${source.contractId}-${source.letter}`} className="purchase-map__key">
                                <span className="purchase-map__chip-pin purchase-map__chip-pin--source">
                                    {source.letter}
                                </span>
                                <span className="purchase-map__key-body">
                                    <span className="purchase-map__alias">
                                        {t('alias.source', { n: source.aliasIndex })}
                                    </span>
                                    <span className="purchase-map__coords">
                                        {formatCoordinates(source.coordinates)}
                                        {source.distanceKm !== undefined && ` · ${source.distanceKm.toFixed(1)} km`}
                                    </span>
                                </span>
                            </li>
                        ))}
                        {focusedSources.length === 0 && (
                            <li className="purchase-map__empty">{t('peta.no_position')}</li>
                        )}
                    </ul>
                ) : (
                    <div className="purchase-map__types">
                        {TRACEABILITY_TYPES.map(item => (
                            <span key={item.value} className="purchase-map__type">
                                <i className="purchase-map__swatch" style={{ background: item.color }} />
                                {item.label}
                            </span>
                        ))}
                    </div>
                )}

                <div className="purchase-map__footnote">
                    {selectedContract && (
                        <span className="purchase-map__type">
                            <i className="purchase-map__swatch purchase-map__swatch--source" />
                            {t('peta.legend.source')}
                        </span>
                    )}
                    <span className="purchase-map__type">
                        <i className="purchase-map__swatch purchase-map__swatch--mill" />
                        {t('peta.legend.mill')}
                    </span>
                    <span className="purchase-map__type">
                        <i className="purchase-map__swatch purchase-map__swatch--flow" />
                        {t('peta.legend.flow')}
                    </span>
                    {!selectedContract && zoneKm > 0 && (
                        <span className="purchase-map__type">
                            <i className="purchase-map__swatch purchase-map__swatch--zone" />
                            {t('peta.legend.zone', { km: zoneKm.toFixed(1) })}
                        </span>
                    )}
                    {avgKm !== undefined && (
                        <span className="purchase-map__type purchase-map__type--metric">
                            {t('peta.legend.avg_distance', { km: avgKm.toFixed(1) })}
                        </span>
                    )}
                </div>
            </div>
        </section>
    );
}
